import * as React from 'react';
import { useState } from 'react';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { motion } from 'motion/react';
import { Mail, Lock, User, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Logo } from '../Logo';

interface RegisterPageProps {
  onSwitchToLogin: () => void;
  onOpenLegal?: (page: 'terms' | 'privacy') => void;
}

export const RegisterPage: React.FC<RegisterPageProps> = ({ onSwitchToLogin, onOpenLegal }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreedToTerms) {
      setError('You must agree to the Terms of Service and Privacy Policy to create an account.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      try {
        await sendEmailVerification(user);
      } catch (ve) {
        console.error('Verification email failed', ve);
      }

      // Create user profile in Firestore
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email: user.email,
        displayName: displayName,
        status: 'pending',
        role: 'user',
        createdAt: new Date().toISOString()
      });

      setSuccess(true);
    } catch (err: any) {
      console.error("Registration error:", err);
      setError(err.message || "Failed to register. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full pl-11 pr-4 py-3 bg-surface-2 border border-ink-200 rounded-md text-ink text-sm placeholder:text-ink-500 focus:border-oxblood focus:ring-2 focus:ring-oxblood/15 focus:bg-surface outline-none transition-all";
  const primaryBtn = "w-full py-3 bg-oxblood hover:bg-oxblood-800 text-paper text-sm font-medium tracking-wide rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.99]";

  if (success) {
    return (
      <div className="w-full max-w-md mx-auto p-4 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="oi-overlay w-full p-8 md:p-10 text-center"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[var(--color-success-50)] rounded-lg mb-7">
            <CheckCircle2 className="w-8 h-8 text-success-600" />
          </div>
          <h1 className="text-2xl font-serif font-semibold text-ink mb-4">Request Submitted</h1>
          <p className="text-ink-600 leading-relaxed mb-9 text-sm">
            Your account has been created and is now pending approval.
            The administrator will review your request shortly. A verification link has also been sent to your email address (please check your spam folder).
          </p>
          <button onClick={onSwitchToLogin} className={primaryBtn}>
            Back to Login
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto p-4 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="oi-overlay w-full p-8 md:p-10"
      >
        <div className="text-center mb-9">
          <div className="flex justify-center mb-6">
            <Logo variant="mark" size={54} />
          </div>
          <h1 className="text-2xl font-serif font-semibold text-ink">Request Access</h1>
          <p className="text-xs font-medium uppercase tracking-[0.14em] text-ink-500 mt-3">Join the Oracle Insight community</p>
        </div>

        {error && (
          <div className="mb-7 p-4 bg-[var(--color-danger-50)] border border-[var(--color-danger-100)] rounded-md flex items-start gap-3">
            <AlertCircle className="w-4 h-4 text-danger-600 shrink-0 mt-0.5" />
            <p className="text-xs text-danger-600 leading-relaxed">{error}</p>
          </div>
        )}

        <form onSubmit={handleRegister} className="space-y-5">
          <div>
            <label className="block text-xs font-medium text-ink-600 mb-2">Full Name</label>
            <div className="relative">
              <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500" />
              <input
                type="text"
                required
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className={inputClass}
                placeholder="John Doe"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-ink-600 mb-2">Email Address</label>
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputClass}
                placeholder="name@company.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-ink-600 mb-2">Password</label>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500" />
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputClass}
                placeholder="••••••••"
              />
            </div>
          </div>

          <div className="flex items-start gap-3">
            <input
              id="terms-checkbox"
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-ink-300 accent-oxblood cursor-pointer"
              required
            />
            <label htmlFor="terms-checkbox" className="text-sm text-ink-600 leading-snug">
              I agree to the{' '}
              <button
                type="button"
                onClick={() => onOpenLegal?.('terms')}
                className="text-oxblood hover:underline font-medium"
              >
                Terms of Service
              </button>{' '}
              and{' '}
              <button
                type="button"
                onClick={() => onOpenLegal?.('privacy')}
                className="text-oxblood hover:underline font-medium"
              >
                Privacy Policy
              </button>
              .
            </label>
          </div>

          <button type="submit" disabled={loading || !agreedToTerms} className={primaryBtn}>
            {loading ? "Submitting..." : "Request Access"}
          </button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-xs text-ink-500">
            Already have an account?{" "}
            <button
              onClick={onSwitchToLogin}
              className="text-oxblood font-medium hover:underline ml-1"
            >
              Sign In
            </button>
          </p>
        </div>
      </motion.div>
    </div>
  );
};
